/**
 * Import React libraries.
 */
import * as React from 'react';


/**
 * Import custom styles.
 */
import {COLORS, ComponentSStyling, focusStyles} from '../styles';
import {NoteMessage, readOnlyState, ValidationMessage} from './styling';
import { States } from '../types';

/**
 * Import custom components.
 */
import { SystemIcons } from '../icons';

/**
 * Import third-party libraries.
 */
import styled, { useTheme } from 'styled-components';
import { ComponentMStyling, ComponentTextStyle } from '../styles';
import { TextareaProps, TextFieldNote } from './types';
import {useFocusVisibleRef} from "../common";

// Add component-specific styles.
const TextareaWrapper = styled.div<{ $margin?: string }>`
  width: 100%;
  ${(props) => (props.$margin ? `margin: ${props.$margin};` : '')}
`;
const TextArea = styled.textarea<{ $autoHeight?: boolean }>`
  display: block;
  background: ${props => COLORS.generateToken({ componentType:'bg-surface', defaultVariant:'default' }, props.theme)};
  border-radius: 4px;
  outline: none;
  margin-bottom: 4px;
  padding: 16px;
  border: none;
  box-shadow: inset 0 0 0 1px ${props => COLORS.generateToken({ componentType:'border', defaultVariant:'default' }, props.theme)};

  resize: ${(props) => (props.$autoHeight ? 'none' : '')};

  ${props => ComponentMStyling(ComponentTextStyle.Regular, COLORS.generateToken({ componentType: 'text', defaultVariant: 'default' }, props.theme))}
  &.small {
    ${props => ComponentSStyling(ComponentTextStyle.Regular, COLORS.generateToken({ componentType: 'text', defaultVariant: 'default' }, props.theme))}
    width: 304px;

    &::placeholder {
      ${props => ComponentSStyling(ComponentTextStyle.Italic, COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme))}
    }
  }

  &.medium {
    ${props => ComponentMStyling(ComponentTextStyle.Regular, COLORS.generateToken({ componentType: 'text', defaultVariant: 'default' }, props.theme))}
    &::placeholder {
      ${props => ComponentMStyling(ComponentTextStyle.Italic, COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme))}
    }
    width: 344px;
  }

  &:focus:not(.focus-visible) {
    box-shadow: inset 0 0 0 2px ${props => COLORS.getColor('primary_800', props.theme)};
  }

  &.focus-visible {
    ${focusStyles}
  }

  &.invalid {
    box-shadow: inset 0 0 0 2px ${props => COLORS.generateToken({ componentType: 'border', defaultVariant: 'critical' }, props.theme)};
  }

  &.valid {
    box-shadow: inset 0 0 0 2px ${props => COLORS.generateToken({ componentType: 'border', defaultVariant: 'positive' }, props.theme)};
  }

  &:hover {
    box-shadow: inset 0 0 0 2px ${props => COLORS.generateToken({ componentType: 'border', state: 'hover' }, props.theme)};
  }

  &.active {
    box-shadow: inset 0 0 0 2px ${props => COLORS.generateToken({ componentType: 'border', state: 'active' }, props.theme)};
  }

  &:read-only:not(:disabled) {
    ${readOnlyState}
  }

  &:disabled {
    cursor: not-allowed;
    border: 1px solid ${props => COLORS.generateToken({ componentType: 'border', state: 'disabled' }, props.theme)};
    box-shadow: none !important;
    color: ${props => COLORS.generateToken({ componentType: 'text', state: 'disabled' }, props.theme)} !important;

    &::placeholder {
      color: ${props => COLORS.generateToken({ componentType: 'text', state: 'disabled' }, props.theme)};
    }
  }

  &::placeholder {
    ${props => ComponentMStyling(ComponentTextStyle.Italic, COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme))}
  }
`;

const Textarea = React.forwardRef<any, TextareaProps>(({
  id,
  placeholder,
  state,
  value,
  size,
  disabled,
  validationMessage,
  onChange,
  margin,
  autoHeight,
  note,
  required,
  className,
  dataTestId,
  ...rest
}: TextareaProps, ref) => {

  const elementRef = useFocusVisibleRef();
  const theme = useTheme();

  React.useImperativeHandle(ref, () => elementRef.current, [elementRef]);

  const cls = `${size} ${state || (validationMessage ? States.Invalid : '')} ${className || ''}`;
  return (
    <TextareaWrapper>
      <TextArea
        id={id}
        data-testid={dataTestId}
        ref={elementRef}
        value={value}
        placeholder={disabled ? undefined : placeholder}
        $autoHeight={autoHeight}
        className={cls}
        disabled={disabled}
        onChange={(e: any) => onChange && onChange(e?.target?.value || '')}
        required={required}
        {...rest}
      />
      {validationMessage && (
        <ValidationMessage className={size || ''} type={state ?? States.Invalid}>
          {state === States.Valid ? <SystemIcons.CheckMark color={COLORS.generateToken({componentType:'text', defaultVariant:'positive'}, theme)} /> : 
            <SystemIcons.TechnicalWarning color={COLORS.generateToken({componentType:'text', defaultVariant:'critical'}, theme)} />}
          <span>{validationMessage}</span>
        </ValidationMessage>
      )}
      {note && !disabled && (
        <NoteMessage className={size}>
          {note.icon}
          <span>{note.message}</span>
        </NoteMessage>
      )}
    </TextareaWrapper>
  );
});

export default Textarea;
